import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ArrowLeft,
  BookOpen,
  CalendarCheck,
  Clock,
  MapPin,
  Monitor,
  User,
  Video,
  XCircle,
} from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { formatDate } from '../lib/format';
import {
  SLOT_MODE_LABEL,
  type CandidateInterviewSlot,
  type CandidateStatusResponse,
} from '../components/hiring';

export const Route = createFileRoute('/_authed/candidate/interview/book')({
  component: BookInterviewPage,
});

/**
 * H-5: the slots endpoint now surfaces the interviewer's display name on each
 * bookable card (display name only — never id/email/phone). The shared
 * `CandidateInterviewSlot` type does not yet carry this field, so we extend it
 * locally; once the field lands in the shared type this `extends` resolves to
 * the same member with no churn here.
 */
interface BookableSlot extends CandidateInterviewSlot {
  interviewerName: string | null;
}

interface BookableSlotsResponse {
  items: BookableSlot[];
  reason?: string;
}

/**
 * H-2: owner-editable prep copy shown above the slot calendar. Mirrors the
 * server `HiringInterviewPrepConfig` shape (all fields nullable markdown-light
 * strings); rendered with `whitespace-pre-line` like other owner copy in the
 * portal (OnboardingTerms), which is XSS-safe because React escapes text.
 */
interface InterviewPrepConfig {
  liveInterviewMarkdown: string | null;
  setupTestMarkdown: string | null;
  howToPrepareMarkdown: string | null;
}

const DEFAULT_PREP_CONFIG: InterviewPrepConfig = {
  liveInterviewMarkdown: null,
  setupTestMarkdown: null,
  howToPrepareMarkdown: null,
};

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const dayKey = (iso: string): string => {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  });
};

function BookInterviewPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const statusQuery = useQuery({
    queryKey: ['me', 'hiring', 'status'] as const,
    queryFn: ({ signal }) =>
      apiRequest<CandidateStatusResponse>('/api/me/hiring/status', { signal }),
  });

  const slotsQuery = useQuery({
    queryKey: ['me', 'hiring', 'slots'] as const,
    queryFn: ({ signal }) =>
      apiRequest<BookableSlotsResponse>('/api/me/hiring/interview/slots', {
        signal,
      }),
  });

  // H-2: prep guidance is non-blocking — a failure here must never hide the
  // calendar. On error or null we fall back to DEFAULT_PREP_CONFIG, which the
  // PrepDetails component renders as neutral fallback paragraphs.
  const prepQuery = useQuery({
    queryKey: ['hiring', 'interview-prep-config'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ config: InterviewPrepConfig }>(
        '/api/hiring/interview-prep-config',
        { signal },
      ),
    staleTime: 60_000,
  });

  const bookMutation = useMutation({
    mutationFn: (slotId: string) =>
      apiRequest<{ id: string }>('/api/me/hiring/interview/book', {
        method: 'POST',
        body: { slotId },
      }),
    onSuccess: () => {
      toast('Interview booked. Watch your inbox for confirmation.', {
        variant: 'success',
      });
      void queryClient.invalidateQueries({ queryKey: ['me', 'hiring'] });
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not book the slot.',
        { variant: 'error' },
      ),
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>('/api/me/hiring/interview/cancel', {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      toast('Interview cancelled.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['me', 'hiring'] });
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not cancel.', {
        variant: 'error',
      }),
  });

  const status = statusQuery.data;
  const stage = status?.application.pipelineStage;
  const alreadyBooked = stage === 'interview_booked';

  const handleBook = async (slot: BookableSlot) => {
    const ok = await confirm({
      title: 'Confirm booking',
      description: `You'll be interviewed on ${formatDate(slot.startsAt, { withTime: true })} (${SLOT_MODE_LABEL[slot.mode]}). We'll email you with details.`,
      confirmLabel: 'Book this slot',
    });
    if (!ok) return;
    bookMutation.mutate(slot.id);
  };

  const handleCancel = async () => {
    const ok = await confirm({
      title: 'Cancel your interview?',
      description:
        "We'll move you back to eligible-to-book so you can pick a different time.",
      confirmLabel: 'Cancel interview',
      variant: 'danger',
    });
    if (!ok) return;
    cancelMutation.mutate();
  };

  const grouped = useMemo(() => {
    const slots = slotsQuery.data?.items ?? [];
    const map = new Map<string, BookableSlot[]>();
    for (const s of [...slots].sort((a, b) => (a.startsAt < b.startsAt ? -1 : 1))) {
      const key = dayKey(s.startsAt);
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    return map;
  }, [slotsQuery.data]);

  const prepConfig = prepQuery.data?.config ?? DEFAULT_PREP_CONFIG;

  return (
    <ListShell
      title={alreadyBooked ? 'Your interview' : 'Book your interview'}
      description={
        alreadyBooked
          ? 'You can cancel or reschedule below — please give us a heads-up at least 24 hours before the slot.'
          : 'Pick a slot that suits you. We will email a meeting link or location.'
      }
      primaryAction={
        <Link
          to="/candidate/status"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to my application
        </Link>
      }
    >
      <GatedSection
        error={slotsQuery.error ?? statusQuery.error}
        title="Interview booking"
        onRetry={() => {
          void slotsQuery.refetch();
          void statusQuery.refetch();
        }}
      >
        {statusQuery.isLoading || slotsQuery.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : alreadyBooked ? (
          <div className="space-y-6">
            <BookedView
              booking={status?.timeline.find(
                (e) => e.toStage === 'interview_booked',
              )}
              onCancel={handleCancel}
              cancelling={cancelMutation.isPending}
            />
            <InterviewPrepDetails
              config={prepConfig}
              loading={prepQuery.isLoading}
            />
          </div>
        ) : (
          <div className="space-y-6">
            <InterviewPrepDetails
              config={prepConfig}
              loading={prepQuery.isLoading}
            />
            {grouped.size === 0 ? (
              <EmptyState
                icon={<Clock size={28} aria-hidden="true" />}
                title="No slots available yet"
                description={
                  slotsQuery.data?.reason === 'not_eligible_to_book'
                    ? "You're not currently eligible to book — please check the status page."
                    : "We're working on opening more interview slots. Please check back tomorrow."
                }
              />
            ) : (
              Array.from(grouped.entries()).map(([day, slots]) => (
                <section key={day}>
                  <h2 className="font-display text-base font-semibold text-[color:var(--color-text)]">
                    {day}
                  </h2>
                  <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {slots.map((slot) => (
                      <li key={slot.id}>
                        <SlotCard
                          slot={slot}
                          onBook={() => handleBook(slot)}
                          booking={bookMutation.isPending}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>
        )}
      </GatedSection>
    </ListShell>
  );
}

interface BookedViewProps {
  booking:
    | { createdAt: string; toStage: string }
    | undefined;
  onCancel: () => void;
  cancelling: boolean;
}

function BookedView({ booking, onCancel, cancelling }: BookedViewProps) {
  return (
    <section className="mx-auto max-w-xl space-y-4 rounded-2xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] p-6 dark:from-gold-700/20">
      <div className="flex items-start gap-3">
        <span className="rounded-full bg-gold-100 p-2 text-gold-800 dark:bg-gold-700/40 dark:text-gold-50">
          <CalendarCheck size={20} aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-display text-xl font-semibold">
            Your interview is booked
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
            We sent the details to your email. If you need to change the time,
            cancel here first and pick a new slot.
          </p>
          {booking ? (
            <p className="mt-2 text-xs text-[color:var(--color-text-muted)] tabular-nums">
              Booked {formatDate(booking.createdAt, { withTime: true })}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="danger"
          icon={<XCircle size={14} aria-hidden="true" />}
          loading={cancelling}
          onClick={onCancel}
        >
          Cancel interview
        </Button>
      </div>
    </section>
  );
}

interface SlotCardProps {
  slot: BookableSlot;
  onBook: () => void;
  booking: boolean;
}

function SlotCard({ slot, onBook, booking }: SlotCardProps) {
  return (
    <article className="flex h-full flex-col gap-2 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4 transition-colors hover:border-gold-500">
      <header className="flex items-start justify-between">
        <p className="font-display text-base font-semibold tabular-nums">
          {formatTime(slot.startsAt)} – {formatTime(slot.endsAt)}
        </p>
        <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
          {slot.capacityRemaining} open
        </span>
      </header>
      <p className="inline-flex items-center gap-1 text-xs text-[color:var(--color-text-muted)]">
        {slot.mode === 'online' ? (
          <Monitor size={12} aria-hidden="true" />
        ) : (
          <MapPin size={12} aria-hidden="true" />
        )}
        {SLOT_MODE_LABEL[slot.mode]}
        {slot.locationOrLink ? ` · ${slot.locationOrLink}` : ''}
      </p>
      {/* H-5: display name only — never the interviewer's id/email/phone. */}
      {slot.interviewerName ? (
        <p className="inline-flex items-center gap-1 text-xs text-[color:var(--color-text-muted)]">
          <User size={12} aria-hidden="true" />
          Interviewer: {slot.interviewerName}
        </p>
      ) : null}
      <div className="mt-auto flex justify-end pt-2">
        <Button size="sm" onClick={onBook} loading={booking}>
          Book this slot
        </Button>
      </div>
    </article>
  );
}

/**
 * H-2: three prep blocks shown above the slot calendar — what the live
 * interview involves, the location/setup check, and how to prepare. Copy is
 * owner-editable (fetched from `/api/hiring/interview-prep-config`); each block
 * renders the stored copy when present, or a neutral fallback paragraph when
 * null so the candidate always has something to read. Never blank.
 */
function InterviewPrepDetails({
  config,
  loading,
}: {
  config: InterviewPrepConfig;
  loading: boolean;
}) {
  if (loading) {
    return <Skeleton className="h-48 w-full" />;
  }

  return (
    <section className="space-y-4 rounded-2xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] p-5 dark:from-gold-700/20">
      <header className="flex items-start gap-3">
        <span className="rounded-full bg-gold-100 p-2 text-gold-800 dark:bg-gold-700/40 dark:text-gold-50">
          <Video size={18} aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-display text-lg font-semibold">
            Before your interview
          </h2>
          <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
            What to expect, how to set up your device, and how to prepare.
          </p>
        </div>
      </header>
      <div className="grid gap-3">
        <PrepBlock
          icon={Video}
          title="The live interview"
          body={config.liveInterviewMarkdown}
          fallback="Your interviewer will walk you through the format on the day. Expect a friendly conversation about your background, your teaching approach, and your availability."
        />
        <PrepBlock
          icon={Monitor}
          title="Camera & internet setup check"
          body={config.setupTestMarkdown}
          fallback="If your interview is online, we'll check your camera, microphone, and internet connection at the start of the call. Join from a quiet, well-lit room and test that your camera and mic work beforehand."
        />
        <PrepBlock
          icon={BookOpen}
          title="How to prepare"
          body={config.howToPrepareMarkdown}
          fallback="No advance reading is required. Bring a quiet space, a working device, and a few minutes to spare before the start time."
        />
      </div>
    </section>
  );
}

function PrepBlock({
  icon: Icon,
  title,
  body,
  fallback,
}: {
  icon: React.ComponentType<{
    size?: number;
    'aria-hidden'?: boolean | 'true' | 'false';
  }>;
  title: string;
  body: string | null;
  fallback: string;
}) {
  const text = body && body.trim().length > 0 ? body : fallback;
  const isFallback = !(body && body.trim().length > 0);
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-3">
      <div className="flex items-center gap-2 text-[color:var(--color-text)]">
        <Icon size={14} aria-hidden="true" />
        <h3 className="text-sm font-medium">{title}</h3>
      </div>
      <p
        className={
          isFallback
            ? 'mt-1.5 whitespace-pre-line text-xs text-[color:var(--color-text-muted)]'
            : 'mt-1.5 whitespace-pre-line text-sm text-[color:var(--color-text)]'
        }
      >
        {text}
      </p>
    </div>
  );
}

